import { useEffect, useRef, useState } from "react";

import type { PnfColumn, PnfMethod } from "@/features/wyckoff/pnf";

const CANVAS_TEXT = "#cbd4e2";
const CANVAS_MUTED = "#7f8ba1";
const CANVAS_GRID = "rgba(182, 191, 204, 0.12)";
const CANVAS_FILL = "#081018";

interface PointFigureCanvasProps {
  columns: PnfColumn[];
  boxSize: number;
  method: PnfMethod;
  className?: string;
}

export function PointFigureCanvas({ columns, boxSize, method, className }: PointFigureCanvasProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [wrapSize, setWrapSize] = useState({ width: 0, height: 0 });
  const safeBoxSize = Number.isFinite(boxSize) && boxSize > 0 ? boxSize : 0.5;

  useEffect(() => {
    if (!wrapRef.current) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const width = Math.max(0, Math.floor(entries[0]?.contentRect.width || 0));
      const height = Math.max(0, Math.floor(entries[0]?.contentRect.height || 0));
      setWrapSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
    });
    observer.observe(wrapRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    const targetWidth = Math.max(320, wrapSize.width || 0);
    const targetHeight = Math.max(220, wrapSize.height || 0);
    if (!columns.length) {
      drawEmpty(canvas, ctx, targetWidth, targetHeight);
      return;
    }

    const maxPrice = Math.max(...columns.map((item) => item.top));
    const minPrice = Math.min(...columns.map((item) => item.bottom));
    const rows = Math.max(2, Math.round((maxPrice - minPrice) / safeBoxSize) + 1);
    const cols = Math.max(1, columns.length);

    const leftPad = 56;
    const topPad = 20;
    const rightPad = 8;
    const bottomPad = 10;
    const cssW = targetWidth;
    const cssH = targetHeight;
    const drawW = Math.max(10, cssW - leftPad - rightPad);
    const drawH = Math.max(10, cssH - topPad - bottomPad);
    const cellW = drawW / cols;
    const cellH = drawH / rows;
    const centerCellY = topPad + cellH / 2;

    const dpr = window.devicePixelRatio || 1;
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.clearRect(0, 0, cssW, cssH);
    ctx.fillStyle = CANVAS_FILL;
    ctx.fillRect(0, 0, cssW, cssH);

    ctx.strokeStyle = CANVAS_GRID;
    ctx.lineWidth = 1;
    for (let c = 0; c <= cols; c += 1) {
      const x = leftPad + c * cellW;
      ctx.beginPath();
      ctx.moveTo(x, topPad);
      ctx.lineTo(x, topPad + drawH);
      ctx.stroke();
    }
    for (let r = 0; r <= rows; r += 1) {
      const y = topPad + r * cellH;
      ctx.beginPath();
      ctx.moveTo(leftPad, y);
      ctx.lineTo(leftPad + drawW, y);
      ctx.stroke();
    }

    ctx.fillStyle = CANVAS_MUTED;
    ctx.font = '11px "JetBrains Mono", "Noto Sans SC", sans-serif';
    const labelStep = Math.max(1, Math.round(16 / Math.max(1, cellH)));
    for (let r = 0; r < rows; r += 1) {
      if (r % labelStep !== 0 && r !== rows - 1) {
        continue;
      }
      const price = maxPrice - r * safeBoxSize;
      ctx.fillText(formatPrice(price, safeBoxSize), 6, topPad + r * cellH + 4);
    }

    const rowIndex = (price: number) => Math.round((maxPrice - price) / safeBoxSize);
    const useGlyph = Math.min(cellW, cellH) >= 7;
    ctx.font = `${Math.max(8, Math.min(12, cellH - 2))}px "JetBrains Mono", sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    columns.forEach((column, columnIndex) => {
      const x = leftPad + columnIndex * cellW + cellW / 2;
      if (column.type === "X") {
        ctx.fillStyle = "#dd5b42";
        for (let price = column.bottom + safeBoxSize; price <= column.top + safeBoxSize * 0.2; price += safeBoxSize) {
          const r = rowIndex(price);
          if (r >= 0 && r < rows) {
            const y = centerCellY + r * cellH;
            if (useGlyph) {
              ctx.fillText("X", x, y);
            } else {
              ctx.fillRect(x - Math.max(1, cellW * 0.24), y - Math.max(1, cellH * 0.24), Math.max(1, cellW * 0.48), Math.max(1, cellH * 0.48));
            }
          }
        }
      } else {
        ctx.fillStyle = "#0f9d7a";
        for (let price = column.top - safeBoxSize; price >= column.bottom - safeBoxSize * 0.2; price -= safeBoxSize) {
          const r = rowIndex(price);
          if (r >= 0 && r < rows) {
            const y = centerCellY + r * cellH;
            if (useGlyph) {
              ctx.fillText("O", x, y);
            } else {
              ctx.fillRect(x - Math.max(1, cellW * 0.24), y - Math.max(1, cellH * 0.24), Math.max(1, cellW * 0.48), Math.max(1, cellH * 0.48));
            }
          }
        }
      }
    });

    ctx.textAlign = "left";
    ctx.fillStyle = CANVAS_TEXT;
    ctx.font = '12px "Noto Sans SC", sans-serif';
    ctx.fillText(`P&F (${method === "hilo" ? "高低价法" : "收盘价法"})`, leftPad + 4, 13);
  }, [columns, method, safeBoxSize, wrapSize.height, wrapSize.width]);

  return (
    <div ref={wrapRef} className={className || "h-full min-h-[220px] w-full rounded border border-[var(--border)] bg-[#0a0f16]"}>
      <canvas ref={canvasRef} className="block" />
    </div>
  );
}

function drawEmpty(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, width: number, height: number) {
  const cssW = Math.max(width, 320);
  const cssH = Math.max(height, 220);
  const dpr = window.devicePixelRatio || 1;
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;
  canvas.width = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);
  ctx.fillStyle = CANVAS_FILL;
  ctx.fillRect(0, 0, cssW, cssH);
  ctx.fillStyle = CANVAS_MUTED;
  ctx.font = '14px "Noto Sans SC", sans-serif';
  ctx.fillText("点数图列不足，请调整可视范围或 box 参数。", 16, 34);
}

function formatPrice(value: number, boxSize: number) {
  if (!Number.isFinite(value)) {
    return "--";
  }
  const absBox = Math.abs(boxSize);
  const decimals = absBox < 0.1 ? 3 : absBox < 1 ? 2 : 1;
  return value.toFixed(decimals);
}
